import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { showFieldMessageBubbleOnMap } from '../utils/dashboard/fieldMessageHelpers';
import {
  upsertFieldMessagePinOnMap,
  removeFieldMessagePinFromMap,
  reprojectFieldMessagePins,
  clearAllFieldMessagePins,
  fetchActiveFieldMessagePins
} from '../utils/dashboard/fieldMessagePinHelpers';
import { hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';
import { getCurrentUserId } from '../utils/userSettings';
import { getCurrentSystemRole } from '../utils/systemRoles';
import { isVoicePttPayload, playVoicePttFromPayload } from '../utils/dashboard/voicePttPlayback';

const NORMAL_BANNER_MS = 15000;
const MAX_VISIBLE_BANNERS = 3;

function memberKey(memberOrPayload) {
  return memberOrPayload?.device_unique_id || memberOrPayload?.name || '';
}

function createBannerId() {
  return `banner_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * 指挥页接收轻量 App 前线消息：
 * - 队员坐标方形气泡（可自动消失）
 * - 勾选「附带坐标打点」时：持久地图点，仅手动删除
 * - 地图正上方可叠放公告条
 * - 按队员分组的会话历史
 */
export default function useDashboardFieldMessages({ getMap, getMembers, onOpenMemberChat, onFlyToMember, onVoicePttReceived, isMemberChatOpen, shouldAutoPlayVoicePtt, onVoicePttAutoPlayResult }) {
  const [fieldMessageBanners, setFieldMessageBanners] = useState([]);
  const [messageMap, setMessageMap] = useState({});
  /** 递增后对话窗重拉服务端历史（结束任务 / 删消息） */
  const [historyEpoch, setHistoryEpoch] = useState(0);
  const bubblesRef = useRef([]);
  const pinsMapRef = useRef(new Map());
  const bannerTimersRef = useRef(new Map());

  const clearBubbles = useCallback(() => {
    bubblesRef.current.forEach((b) => b?.remove?.());
    bubblesRef.current = [];
  }, []);

  const clearAllBannerTimers = useCallback(() => {
    bannerTimersRef.current.forEach((timer) => clearTimeout(timer));
    bannerTimersRef.current.clear();
  }, []);

  const dismissFieldMessageBanner = useCallback((bannerId) => {
    if (!bannerId) return;
    const timer = bannerTimersRef.current.get(bannerId);
    if (timer) {
      clearTimeout(timer);
      bannerTimersRef.current.delete(bannerId);
    }
    setFieldMessageBanners((prev) => prev.filter((b) => b.bannerId !== bannerId));
  }, []);

  useEffect(() => () => {
    clearBubbles();
    clearAllBannerTimers();
    clearAllFieldMessagePins(pinsMapRef.current);
  }, [clearBubbles, clearAllBannerTimers]);

  const appendMemberMessage = useCallback((entry) => {
    if (!entry?.text && !(Array.isArray(entry.media) && entry.media.length)) return;
    const keys = [];
    const idKey = entry?.device_unique_id ? String(entry.device_unique_id) : '';
    const nameKey = entry?.name ? String(entry.name) : '';
    if (idKey) keys.push(idKey);
    if (nameKey && nameKey !== idKey) keys.push(nameKey);
    if (!keys.length) return;
    const normalized = {
      id: entry.id || `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      direction: entry.direction || 'in',
      text: entry.text,
      type: entry.type,
      important: !!entry.important,
      pin_on_map: !!entry.pin_on_map,
      name: entry.name,
      device_unique_id: entry.device_unique_id,
      group_name: entry.group_name,
      created_at: entry.created_at || new Date().toISOString(),
      created_by: entry.created_by,
      created_by_label: entry.created_by_label,
      reply_scope: entry.reply_scope,
      reply_to_label: entry.reply_to_label,
      batch_id: entry.batch_id,
      command_id: entry.command_id,
      command_kind: entry.command_kind,
      latitude: entry.latitude,
      longitude: entry.longitude,
      media: Array.isArray(entry.media) && entry.media.length ? entry.media : undefined
    };
    setMessageMap((prev) => {
      let next = prev;
      let changed = false;
      keys.forEach((key) => {
        const list = next[key] || [];
        if (list.some((m) => m.id === normalized.id && m.text === normalized.text)) return;
        if (!changed) { next = { ...next }; changed = true; }
        next[key] = [...list, normalized].slice(-80);
      });
      return changed ? next : prev;
    });
  }, []);

  const getMessagesForMember = useCallback((member) => {
    if (!member) return [];
    const idKey = member.device_unique_id ? String(member.device_unique_id) : '';
    const nameKey = member.name ? String(member.name) : '';
    const buckets = [];
    if (idKey && messageMap[idKey]) buckets.push(messageMap[idKey]);
    if (nameKey && messageMap[nameKey] && messageMap[nameKey] !== messageMap[idKey]) {
      buckets.push(messageMap[nameKey]);
    }
    // 兼容：上行仍挂在旧 zhxt_app:… 键上时，按 name 扫一遍
    if (nameKey) {
      Object.keys(messageMap).forEach((k) => {
        if (k === idKey || k === nameKey) return;
        const list = messageMap[k];
        if (!Array.isArray(list) || !list.length) return;
        if (list.some((m) => m && String(m.name || '') === nameKey)) buckets.push(list);
      });
    }
    if (!buckets.length) return [];
    const merged = [];
    buckets.forEach((list) => {
      list.forEach((m) => {
        if (!merged.some((x) => x.id === m.id && x.text === m.text)) merged.push(m);
      });
    });
    return merged.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  }, [messageMap]);

  /** 结束任务：清该批队员内存会话 + 气泡，并 bump historyEpoch */
  const clearMessagesForMembers = useCallback((membersOrKeys = []) => {
    const keys = new Set();
    (membersOrKeys || []).forEach((m) => {
      if (!m) return;
      if (typeof m === 'string') {
        if (m.trim()) keys.add(m.trim());
        return;
      }
      if (m.device_unique_id) keys.add(String(m.device_unique_id));
      if (m.name) keys.add(String(m.name));
    });
    if (!keys.size) {
      setMessageMap({});
      setHistoryEpoch((n) => n + 1);
      clearBubbles();
      return;
    }
    setMessageMap((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((k) => {
        if (keys.has(k)) {
          delete next[k];
          changed = true;
          return;
        }
        const list = next[k];
        if (!Array.isArray(list)) return;
        const filtered = list.filter((msg) => {
          const id = msg?.device_unique_id ? String(msg.device_unique_id) : '';
          const name = msg?.name ? String(msg.name) : '';
          return !(id && keys.has(id)) && !(name && keys.has(name));
        });
        if (filtered.length !== list.length) {
          next[k] = filtered;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setHistoryEpoch((n) => n + 1);
    clearBubbles();
  }, [clearBubbles]);

  /** 删除单条：从内存 map 去掉 */
  const removeMessageFromMap = useCallback((item) => {
    if (!item) return;
    const hideIds = new Set();
    if (item.id) hideIds.add(String(item.id));
    if (item.batch_id) hideIds.add(`batch:${item.batch_id}`);
    if (item.command_id != null) hideIds.add(`cmd:${item.command_id}`);
    setMessageMap((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const list = next[k];
        if (!Array.isArray(list)) return;
        const filtered = list.filter((m) => {
          if (!m) return false;
          if (m.id && hideIds.has(String(m.id))) return false;
          if (m.batch_id && hideIds.has(`batch:${m.batch_id}`)) return false;
          if (m.command_id != null && hideIds.has(`cmd:${m.command_id}`)) return false;
          return true;
        });
        if (filtered.length !== list.length) {
          next[k] = filtered;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const dismissBannersForPinId = useCallback((pinId) => {
    if (!pinId) return;
    setFieldMessageBanners((prev) => {
      const removing = prev.filter((b) => b.id === pinId);
      removing.forEach((b) => {
        const timer = bannerTimersRef.current.get(b.bannerId);
        if (timer) {
          clearTimeout(timer);
          bannerTimersRef.current.delete(b.bannerId);
        }
      });
      return prev.filter((b) => b.id !== pinId);
    });
  }, []);

  const placePersistentPin = useCallback((pin) => {
    const map = getMap?.();
    if (!map || !pin?.id) return;
    upsertFieldMessagePinOnMap(map, pin, pinsMapRef.current, {
      onDeleted: (id) => {
        dismissBannersForPinId(id);
        message.success('已删除消息坐标点');
      },
      onDeleteError: (error, id) => {
        // 本地点已移除；绝不弹出「打点表不可用」
        dismissBannersForPinId(id || pin.id);
        message.success('已删除消息坐标点');
      }
    });
  }, [getMap, dismissBannersForPinId]);

  const handleTrackerMessagePinRemoved = useCallback((payload) => {
    const id = payload?.id;
    if (!id) return;
    removeFieldMessagePinFromMap(pinsMapRef.current, id);
    dismissBannersForPinId(id);
  }, [dismissBannersForPinId]);

  const loadFieldMessagePins = useCallback(async () => {
    try {
      const pins = await fetchActiveFieldMessagePins();
      const map = getMap?.();
      if (!map) return;
      // 只补画/刷新持久点，不删本地已有点（避免与刚收到的 WS 打点竞态）
      pins.forEach((pin) => placePersistentPin(pin));
    } catch (error) {
      console.warn('加载消息打点失败:', error.message || error);
    }
  }, [getMap, placePersistentPin]);

  const reprojectFieldMessagePinsOnMap = useCallback((basemapId) => {
    const map = getMap?.();
    if (!map) return;
    reprojectFieldMessagePins(map, pinsMapRef.current, basemapId);
  }, [getMap]);

  const handleTrackerFieldMessage = useCallback((payload) => {
    if (!payload) return;
    const hasMedia = Array.isArray(payload.media) && payload.media.length > 0;
    if (!payload.text && !hasMedia) return;
    // 无「队员指令下达/接收」权限：不展示 APP 回复
    if (!hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND)) return;
    const scope = String(payload.reply_scope || 'everyone').toLowerCase();
    const targetId = payload.reply_to_user_id != null ? Number(payload.reply_to_user_id) : null;
    const myId = getCurrentUserId();
    const isAdmin = getCurrentSystemRole() === 'admin';
    if (scope === 'user' && Number.isFinite(targetId) && !isAdmin && Number(myId) !== targetId) {
      return;
    }
    const members = getMembers?.() || [];
    const member = members.find(
      (m) => m.device_unique_id === payload.device_unique_id || m.name === payload.name
    );
    const bannerId = createBannerId();
    const enriched = {
      ...payload,
      bannerId,
      direction: 'in',
      latitude: payload.latitude ?? member?.last_latitude,
      longitude: payload.longitude ?? member?.last_longitude,
      name: payload.name || member?.name,
      device_unique_id: payload.device_unique_id || member?.device_unique_id,
      group_name: payload.group_name || member?.group_name,
      pin_on_map: !!payload.pin_on_map,
      reply_scope: scope === 'user' ? 'user' : 'everyone',
      reply_to_label: payload.reply_to_label || (scope === 'user' ? '指定指挥员' : '所有指挥员')
    };

    if (!enriched.text) {
      enriched.text = isVoicePttPayload(enriched) ? '[语音]' : (hasMedia ? '[媒体]' : '');
    }

    // 语音对讲：仅当前任务页（或全部列表）自动播；其它任务只记未读，不偷偷播
    if (isVoicePttPayload(enriched)) {
      try {
        onVoicePttReceived?.(enriched);
      } catch (_) { /* ignore */ }
      let allowAuto = true;
      try {
        if (typeof shouldAutoPlayVoicePtt === 'function') {
          allowAuto = shouldAutoPlayVoicePtt(enriched) !== false;
        }
      } catch (_) {
        allowAuto = true;
      }
      if (!allowAuto) {
        try {
          onVoicePttAutoPlayResult?.(enriched, { played: false, reason: 'deferred_other_mission' });
        } catch (_) { /* ignore */ }
        message.info(`收到 ${enriched.name || '队员'} 的语音（非当前任务，已记未读）`);
      } else {
        playVoicePttFromPayload(enriched).then((result) => {
          try {
            onVoicePttAutoPlayResult?.(enriched, result || { played: false });
          } catch (_) { /* ignore */ }
          if (result?.played) {
            if (result.started) {
              message.success(`收到 ${enriched.name || '队员'} 的语音，正在播报`);
            } else if (result.queued) {
              message.success(`收到 ${enriched.name || '队员'} 的语音，已排队播报`);
            }
            return;
          }
          if (result?.reason === 'autoplay_blocked') {
            message.warning(`收到 ${enriched.name || '队员'} 的语音：浏览器拦截自动播放，请点击页面任意处后再收听，或打开队员对话播放`);
            return;
          }
          if (result?.reason === 'skipped') {
            return;
          }
          if (result?.reason && result.reason !== 'no_media') {
            console.warn('[voice_ptt] 指挥端播报未成功:', result.reason, result.error?.message || '');
          }
        }).catch((err) => {
          console.warn('[voice_ptt] 指挥端播报异常:', err?.message || err);
          try {
            onVoicePttAutoPlayResult?.(enriched, { played: false, reason: 'play_error', error: err });
          } catch (_) { /* ignore */ }
        });
      }
    }

    appendMemberMessage(enriched);

    const chatOpen = typeof isMemberChatOpen === 'function'
      ? !!isMemberChatOpen(enriched)
      : false;
    // chat open: skip top banner
    if (!chatOpen) {
      setFieldMessageBanners((prev) => [...prev, enriched]);
    }

    // 顶栏公告条：仍按原逻辑（普通限时消失，重要常驻可手动关）
    // 持久保留仅针对地图坐标点与其气泡，与公告条无关
    if (!chatOpen && !enriched.important) {
      const timer = setTimeout(() => {
        bannerTimersRef.current.delete(bannerId);
        setFieldMessageBanners((prev) => prev.filter((b) => b.bannerId !== bannerId));
      }, NORMAL_BANNER_MS);
      bannerTimersRef.current.set(bannerId, timer);
    }

    const map = getMap?.();

    // 持久打点：地图气泡常驻，仅点「删除」才消失；不叠临时气泡
    if (enriched.pin_on_map) {
      placePersistentPin(enriched);
      return;
    }

    const bubble = showFieldMessageBubbleOnMap(map, enriched, {
      ttlMs: enriched.important ? 0 : undefined,
      onRemove: () => {
        bubblesRef.current = bubblesRef.current.filter((b) => b !== bubble);
      },
      onClick: () => onOpenMemberChat?.(enriched)
    });
    if (bubble) bubblesRef.current.push(bubble);
  }, [appendMemberMessage, getMap, getMembers, onOpenMemberChat, onVoicePttReceived, isMemberChatOpen, shouldAutoPlayVoicePtt, onVoicePttAutoPlayResult, placePersistentPin]);

  const flyToBannerMember = useCallback((msg) => {
    if (!msg) return;
    const members = getMembers?.() || [];
    const member = members.find(
      (m) => m.device_unique_id === msg.device_unique_id || m.name === msg.name
    ) || {
      name: msg.name,
      device_unique_id: msg.device_unique_id,
      last_latitude: msg.latitude,
      last_longitude: msg.longitude
    };
    onFlyToMember?.(member);
  }, [getMembers, onFlyToMember]);

  return {
    handleTrackerFieldMessage,
    handleTrackerMessagePinRemoved,
    clearFieldMessageBubbles: clearBubbles,
    clearMessagesForMembers,
    removeMessageFromMap,
    historyEpoch,
    bumpHistoryEpoch: () => setHistoryEpoch((n) => n + 1),
    getMessagesForMember,
    appendMemberMessage,
    fieldMessageBanners,
    maxVisibleFieldBanners: MAX_VISIBLE_BANNERS,
    dismissFieldMessageBanner,
    flyToFieldMessageMember: flyToBannerMember,
    loadFieldMessagePins,
    reprojectFieldMessagePinsOnMap,
    fieldMessagePinsMapRef: pinsMapRef
  };
}
